import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { message, ConfigProvider, theme } from 'antd';
import {
  DashboardOutlined,
  VideoCameraOutlined,
  BookOutlined,
  CalendarOutlined,
  BranchesOutlined,
  BankOutlined,
  IdcardOutlined,
  TeamOutlined,
  ContactsOutlined,
  MenuOutlined,
  SolutionOutlined,
  ToolOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  HistoryOutlined
} from '@ant-design/icons';
import LoginPage from './Components/Pages/LoginPage/LoginContainer';
import { logout } from './Store/authSlice';
import { API_BASE_URL } from './Service/apiConfig';

// Components
import DashboardContainer from './Components/Pages/DashboardPages/DashboardContainer';
import LiveSessionPage from './Components/Pages/LiveSession/LiveSessionContainer';
import CoursesPage from './Components/Pages/Courses/CoursesContainer';
import SessionManagementPage from './Components/Pages/SessionManagement/SessionManagementContainer';
import ClusterManagementPage from './Components/Pages/ClusterManagement/ClusterManagementContainer';
import InstitutionManagementPage from './Components/Pages/InstitutionManagement/InstitutionManagementContainer';
import AccountsManagementPage from './Components/Pages/AccountsManagement/AccountsManagementContainer';
import InstructorManagementPage from './Components/Pages/InstructorManagement/InstructorManagementPage';
import StaffManagementPage from './Components/Pages/StaffManagement/StaffManagementView';
import CandidateManagementPage from './Components/Pages/CandidateManagement/CandidateManagementContainer';
import SyllabusManagementPage from './Components/Pages/SyllabusManagement/SyllabusManagementContainer';
import EquipmentManagementPage from './Components/Pages/EquipmentManagement/EquipmentManagementContainer';
import PaymentManagementPage from './Components/Pages/PaymentMgmt/PaymentManagementContainer';
import AttendanceManagementPage from './Components/Pages/AttendanceManagement/AttendanceManagementContainer';
import AuditLogPage from './Components/Pages/AuditLogManagement/AuditLogPage';

import logoGif from './Assets/vedika_logo.gif';

// Layout Component
import SidebarLayout from './Components/MainLayout/SidebarLayout';

const routeMap = {
  'dashboard': '/dashboard',
  'live-sessions': '/live-sessions',
  'live-player': '/live-player',
  'courses': '/courses',
  'modules': '/modules',
  'module-videos': '/module-videos',
  'session-mgmt': '/session-management',
  'syllabus': '/syllabus-management',
  'clusters': '/cluster-management',
  'candidates-mgmt': '/candidate-management',
  'institutions': '/institution-management',
  'accounts': '/account-management',
  'instructors': '/instructor-management',
  'staff': '/staff-management',
  'video-player': '/video-player',
  'video-edit': '/video-edit',
  'equipment-mgmt': '/equipment-management',
  'payments': '/payment-management',
  'attendance-mgmt': '/attendance-management',
  'audit-logs': '/audit-logs',
};

const reverseRouteMap = {
  '/dashboard': 'dashboard',
  '/live-sessions': 'live-sessions',
  '/live-player': 'live-player',
  '/courses': 'courses',
  '/modules': 'modules',
  '/module-videos': 'module-videos',
  '/session-management': 'session-mgmt',
  '/cluster-management': 'clusters',
  '/candidate-management': 'candidates-mgmt',
  '/institution-management': 'institutions',
  '/account-management': 'accounts',
  '/instructor-management': 'instructors',
  '/staff-management': 'staff',
  '/video-player': 'video-player',
  '/video-edit': 'video-edit',
  '/syllabus-management': 'syllabus',
  '/equipment-management': 'equipment-mgmt',
  '/payment-management': 'payments',
  '/attendance-management': 'attendance-mgmt',
  '/audit-logs': 'audit-logs',
};

const themeConfig = {
  light: {
    primary: '#10b981',
    bg: '#f3f4f6',
    panel: '#ffffff',
    text: '#1f2937',
    textSecondary: 'rgba(0, 0, 0, 0.45)',
    border: 'rgba(0, 0, 0, 0.06)'
  },
  dark: {
    primary: '#3b82f6',
    bg: '#0b0f19',
    panel: '#0f172a',
    text: '#ffffff',
    textSecondary: 'rgba(255, 255, 255, 0.45)',
    border: 'rgba(255, 255, 255, 0.06)'
  }
};

// Custom breakpoint hook
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < breakpoint);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return isMobile;
}

function App() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, role, user, token } = useSelector((state) => state.auth);
  const isMobile = useIsMobile();

  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebar_collapsed');
    return saved !== null ? saved === 'true' : true;
  });

  const handleSetCollapsed = (val) => {
    setCollapsed(val);
    localStorage.setItem('sidebar_collapsed', String(val));
  };
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState(() => {
    const matchedKey = reverseRouteMap[location.pathname];
    if (matchedKey) return matchedKey;
    const saved = localStorage.getItem('activeMenu');
    if (saved) return saved;
    return 'dashboard';
  });
  const [sessionTimeLeft, setSessionTimeLeft] = useState('');
  const [currentThemeKey, setCurrentThemeKey] = useState(() => {
    const saved = localStorage.getItem('themeKey');
    return (saved === 'light' || saved === 'dark') ? saved : 'light';
  });

  useEffect(() => {
    const matchedKey = reverseRouteMap[location.pathname];
    if (matchedKey) {
      setActiveMenu(matchedKey);
    }
  }, [location.pathname]);

  useEffect(() => {
    localStorage.setItem('themeKey', currentThemeKey);
    const root = document.documentElement;
    const t = themeConfig[currentThemeKey];
    root.style.setProperty('--primary-color', t.primary);
    root.style.setProperty('--bg-color', t.bg);
    root.style.setProperty('--panel-color', t.panel);
    root.style.setProperty('--text-color', t.text);
    root.style.setProperty('--text-secondary', t.textSecondary);
    root.style.setProperty('--border-color', t.border);
    root.setAttribute('data-theme', currentThemeKey);
  }, [currentThemeKey]);

  useEffect(() => {
    if (!isAuthenticated || !token) {
      setSessionTimeLeft('');
      return;
    }

    const parseJwt = (tokenStr) => {
      try {
        const base64Url = tokenStr.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
          window.atob(base64)
            .split('')
            .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
            .join('')
        );
        return JSON.parse(jsonPayload);
      } catch (e) {
        return null;
      }
    };

    const decoded = parseJwt(token);
    if (!decoded || !decoded.exp) {
      setSessionTimeLeft('');
      return;
    }

    const expiryTime = decoded.exp * 1000;

    const updateTimer = () => {
      const now = Date.now();
      const diff = expiryTime - now;
      if (diff <= 0) {
        setSessionTimeLeft('Expired');
        dispatch(logout());
        message.warning('Session has expired.');
      } else {
        const hours = Math.floor(diff / 3600000);
        const minutes = Math.floor((diff % 3600000) / 60000);
        const seconds = Math.floor((diff % 60000) / 1000);
        const hh = String(hours).padStart(2, '0');
        const mm = String(minutes).padStart(2, '0');
        const ss = String(seconds).padStart(2, '0');
        setSessionTimeLeft(`${hh}:${mm}:${ss}`);
      }
    };

    updateTimer();
    const intervalId = setInterval(updateTimer, 1000);
    return () => clearInterval(intervalId);
  }, [isAuthenticated, token, dispatch]);

  const handleLogout = () => {
    localStorage.removeItem('activeMenu');
    dispatch(logout());
  };

  useEffect(() => {
    if (activeMenu) {
      localStorage.setItem('activeMenu', activeMenu);
    }
  }, [activeMenu]);

  useEffect(() => {
    if (isAuthenticated) {
      const checkToken = async () => {
        try {
          const response = await fetch(`${API_BASE_URL}/verify-session`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
          if (response.status === 401 || response.status === 403) {
            dispatch(logout());
            message.warning('Session expired or logged in from another device.');
          }
        } catch (error) {
          console.error('Session check failed', error);
        }
      };
      checkToken();
    }
  }, [isAuthenticated, dispatch]);

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  const handleMenuClick = ({ key }) => {
    setActiveMenu(key);
    const targetPath = routeMap[key] || '/';
    navigate(targetPath);
    setMobileDrawerOpen(false);
  };

  // --- Dynamic Sidebar Menu Items based on Access Control ---
  const getMenuItems = () => {
    const items = [];

    items.push({
      key: 'dashboard',
      icon: <DashboardOutlined />,
      label: 'Dashboards'
    });

    items.push({
      key: 'live-sessions',
      icon: <VideoCameraOutlined />,
      label: 'Live Session'
    });

    items.push({
      key: 'courses',
      icon: <BookOutlined />,
      label: 'Courses'
    });

    if (role === 'ADMIN' || role === 'STUDIO') {
      items.push({
        key: 'session-mgmt',
        icon: <CalendarOutlined />,
        label: 'Session Management'
      });
      items.push({
        key: 'equipment-mgmt',
        icon: <ToolOutlined />,
        label: 'Equipment Management'
      });
      items.push({
        key: 'clusters',
        icon: <BranchesOutlined />,
        label: 'Cluster Management'
      });
      items.push({
        key: 'candidates-mgmt',
        icon: <SolutionOutlined />,
        label: 'Candidates Management'
      });
    }

    if (role === 'ADMIN') {
      items.push({
        key: 'syllabus',
        icon: <BookOutlined />,
        label: 'Syllabus Management'
      });
      items.push({
        key: 'institutions',
        icon: <BankOutlined />,
        label: 'Institution Management'
      });
    }

    if (role === 'ADMIN' || role === 'STUDIO') {
      items.push({
        key: 'accounts',
        icon: <IdcardOutlined />,
        label: 'Accounts Management'
      });
    }

    if (role === 'ADMIN' || role === 'STUDIO' || role === 'COORDINATOR' || role === 'IT-SUPPORT') {
      items.push({
        key: 'attendance-mgmt',
        icon: <ClockCircleOutlined />,
        label: 'Attendance Management'
      });
    }

    if (role === 'ADMIN') {
      items.push({
        key: 'payments',
        icon: <DollarOutlined />,
        label: 'Payment Management'
      });
      items.push({
        key: 'instructors',
        icon: <TeamOutlined />,
        label: "Instructor Management"
      });
      items.push({
        key: 'staff',
        icon: <ContactsOutlined />,
        label: 'Staff Management'
      });
      items.push({
        key: 'audit-logs',
        icon: <HistoryOutlined />,
        label: 'Audit & Activity Logs'
      });
    }

    return items;
  };

  const handleDashboardMenuSelect = (key) => {
    setActiveMenu(key);
    const targetPath = routeMap[key] || '/';
    navigate(targetPath);
  };

  // --- Render Active View Component ---
  const renderContent = () => {
    return (
      <Routes>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<DashboardContainer onMenuSelect={handleDashboardMenuSelect} />} />
        <Route path="/live-sessions" element={<LiveSessionPage userRole={role} currentUser={user} />} />
        <Route path="/live-player" element={<LiveSessionPage userRole={role} currentUser={user} />} />
        <Route path="/courses" element={<CoursesPage userRole={role} />} />
        <Route path="/modules" element={<CoursesPage userRole={role} />} />
        <Route path="/module-videos" element={<CoursesPage userRole={role} />} />
        <Route path="/video-player" element={<CoursesPage userRole={role} />} />
        <Route path="/video-edit" element={<CoursesPage userRole={role} />} />
        <Route path="/session-management" element={<SessionManagementPage userRole={role} />} />
        <Route path="/equipment-management" element={<EquipmentManagementPage />} />
        <Route path="/syllabus-management" element={role === 'ADMIN' ? <SyllabusManagementPage userRole={role} /> : <Navigate to="/" replace />} />
        <Route path="/cluster-management" element={<ClusterManagementPage userRole={role} />} />
        <Route path="/candidate-management" element={<CandidateManagementPage userRole={role} />} />
        <Route path="/institution-management" element={<InstitutionManagementPage />} />
        <Route path="/account-management" element={<AccountsManagementPage userRole={role} />} />
        <Route path="/payment-management" element={<PaymentManagementPage />} />
        <Route path="/instructor-management" element={<InstructorManagementPage />} />
        <Route path="/staff-management" element={<StaffManagementPage />} />
        <Route path="/attendance-management" element={<AttendanceManagementPage userRole={role} />} />
        <Route path="/audit-logs" element={role === 'ADMIN' ? <AuditLogPage userRole={role} /> : <Navigate to="/" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  };

  const activeTheme = themeConfig[currentThemeKey];
  const algorithm = currentThemeKey === 'dark' ? theme.darkAlgorithm : theme.defaultAlgorithm;
  const menuItems = getMenuItems();

  // Bottom nav: show top 5 most relevant items
  const bottomNavItems = menuItems.slice(0, 5);

  const getPageHeading = () => {
    const activeItem = getMenuItems().find((item) => item.key === activeMenu);
    if (activeItem && activeItem.label) return activeItem.label;
    switch (activeMenu) {
      case 'dashboard': return 'Dashboards';
      case 'live-sessions': return 'Live Session';
      case 'courses': return 'Courses';
      case 'session-mgmt': return 'Session Management';
      case 'syllabus': return 'Syllabus Management';
      case 'equipment-mgmt': return 'Equipment Management';
      case 'clusters': return 'Cluster Management';
      case 'candidates-mgmt': return 'Candidates Management';
      case 'institutions': return 'Institution Management';
      case 'accounts': return 'Accounts Management';
      case 'instructors': return 'Instructor Management';
      case 'staff': return 'Staff Management';
      case 'attendance-mgmt': return 'Attendance Management';
      default: return 'Vedika Portal';
    }
  };


  const layoutProps = {
    isMobile,
    collapsed,
    setCollapsed: handleSetCollapsed,
    mobileDrawerOpen,
    setMobileDrawerOpen,
    activeMenu,
    handleMenuClick,
    menuItems,
    user,
    role,
    sessionTimeLeft,
    currentThemeKey,
    setCurrentThemeKey,
    handleLogout,
    logoGif,
    getPageHeading,
    children: renderContent()
  };

  return (
    <ConfigProvider
      theme={{
        algorithm,
        token: {
          colorPrimary: activeTheme.primary,
        }
      }}
    >
      <SidebarLayout {...layoutProps} />

      {/* ===== MOBILE BOTTOM NAV BAR ===== */}
      {isMobile && isAuthenticated && (
        <nav style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          height: 64,
          background: 'var(--panel-color)',
          borderTop: '1px solid var(--border-color)',
          display: 'flex',
          alignItems: 'stretch',
          zIndex: 200,
          boxShadow: '0 -4px 16px rgba(0,0,0,0.18)'
        }}>
          {bottomNavItems.map((item) => {
            const isActive = activeMenu === item.key;
            return (
              <button
                key={item.key}
                onClick={() => handleMenuClick({ key: item.key })}
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 2,
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: isActive ? 'var(--primary-color)' : 'var(--text-secondary)',
                  padding: '4px 2px',
                  borderTop: isActive ? '2px solid var(--primary-color)' : '2px solid transparent',
                  transition: 'all 0.15s ease'
                }}
              >
                <span style={{ fontSize: 20 }}>{item.icon}</span>
                <span style={{
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontSize: 9,
                  fontWeight: isActive ? 700 : 400
                }}>
                  {item.label.split(' ')[0]}
                </span>
              </button>
            );
          })}

          {/* "More" button if there are more than 5 menu items */}
          {menuItems.length > 5 && (
            <button
              onClick={() => setMobileDrawerOpen(true)}
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--text-secondary)',
                padding: '4px 2px',
                borderTop: '2px solid transparent'
              }}
            >
              <MenuOutlined style={{ fontSize: 20 }} />
              <span style={{ fontSize: 9 }}>More</span>
            </button>
          )}
        </nav>
      )}
    </ConfigProvider>
  );
}

export default App;
